import { FormEvent, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { tenantConfig } from '../config/tenant'
import { api, setToken } from '../api/client'
import Logo from '../components/Logo'
import Seo from '../components/Seo'

interface LoginResponse {
  access_token: string
  token_type: string
}

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const navigate = useNavigate()

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const response = await api.post<LoginResponse>('/api/auth/login', { email, password })
      setToken(response.access_token)
      navigate('/dataentry')
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status
      if (status === 401) {
        setError('Invalid email or password. Please check your details.')
      } else if (status === 403) {
        setError('This account is disabled. Contact your administrator.')
      } else {
        setError(err instanceof Error ? err.message : 'Login failed.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6">
      <Seo title="Broker Login" path="/dataentry/login" noindex />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8" aria-label={`${tenantConfig.name} home`}>
          <Logo size={32} />
        </Link>

        <div className="bg-white rounded-2xl border border-[#E8E8E2] shadow-card p-8">
          <h1 className="font-heading text-2xl text-ink mb-1">Broker login</h1>
          <p className="text-sm text-gs-text-muted mb-6">Sign in to manage lenders, products and leads.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium mb-1">Email</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#D8D8D0] rounded-lg text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-medium mb-1">Password</label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#D8D8D0] rounded-lg text-sm"
                required
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm border border-red-200 bg-red-50 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-lg font-semibold text-sm disabled:opacity-50"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>

        <p className="text-xs text-gs-text-muted mt-6 text-center">
          Access is restricted to {tenantConfig.name} team members.
        </p>
      </div>
    </main>
  )
}
